import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import "./TourPackage.css";
import AOS from "aos";
import "aos/dist/aos.css";
import Newsletter from "./Newsletter";
import { CiClock2, CiStar } from "react-icons/ci";
import { IoPricetagOutline, IoPersonOutline } from "react-icons/io5";
import { HiOutlineInformationCircle } from "react-icons/hi2";
import { FaMapSigns, FaImages } from "react-icons/fa";
import { Pane, Text, majorScale, TextInput, Textarea, Select, Button, Spinner, toaster, Tablist, Tab } from "evergreen-ui";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

import {
  PayPalScriptProvider,
  PayPalButtons,
  PayPalMessages,
} from "@paypal/react-paypal-js";


const PAYPAL_CLIENT_ID = process.env.REACT_APP_PAYPAL_CLIENT_ID;
const API_BASE_URL = process.env.REACT_APP_BASE_URL;

/* ----------------------- TABS ----------------------- */
const Tabs = ({ tour }) => {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const tabs = [
    {
      label: "Information",
      icon: (
        <HiOutlineInformationCircle
          style={{ paddingRight: "5px", fontSize: "23px" }}
        />
      ),
    },
    {
      label: "Travel Plan",
      icon: <FaMapSigns style={{ paddingRight: "5px", fontSize: "23px" }} />,
    },
    {
      label: "Gallery",
      icon: <FaImages style={{ paddingRight: "5px", fontSize: "23px" }} />,
    },
  ];

  const renderContent = () => {
    const label = tabs[selectedIndex].label.toLowerCase();

    switch (label) {
      case "information":
        return (
          <Pane>
            <h2 className="title-secondary">Overview</h2>
            <p className="txt_cont">{tour.info_overview}</p>

            <h2 className="title-primary">Highlights</h2>
            {Array.isArray(tour.info_highlight) &&
            tour.info_highlight.length > 0 ? (
              tour.info_highlight.map((h, i) => <p key={i}>{h}</p>)
            ) : (
              <p className="txt_cont">{tour.info_highlight}</p>
            )}

            <table>
              <tbody>
                <tr>
                  <th>Destination:</th>
                  <td className="txt_cont">{tour.destination_name}</td>
                </tr>
                <tr>
                  <th>Departure:</th>
                  <td className="txt_cont">{tour.departure}</td>
                </tr>

                <tr>
                  <th>Included:</th>
                  <td>
                    <ol>
                      {tour.included
                        ? tour.included
                            .split(",")
                            .map((it, i) => <li key={i}>{it.trim()}</li>)
                        : <li>No items included</li>}
                    </ol>
                  </td>
                </tr>

                <tr>
                  <th>Excluded:</th>
                  <td>
                    <ol>
                      {tour.excluded
                        ? tour.excluded
                            .split(",")
                            .map((it, i) => <li key={i}>{it.trim()}</li>)
                        : <li>No items excluded</li>}
                    </ol>
                  </td>
                </tr>
              </tbody>
            </table>
          </Pane>
        );

      case "travel plan":
        return (
          <Pane>
            <h2 className="title-secondary">Travel Plan</h2>
            <p className="txt_cont">{tour.travel_plan_overview}</p>

            <h2 className="title-primary">Day-by-Day</h2>
            {[...Array(7).keys()].map((i) => {
              const titleKey = `travel_plan_day_title_${i + 1}`;
              const descKey = `travel_plan_day_description_${i + 1}`;
              return tour[titleKey] ? (
                <div key={i}>
                  <h3 className="title-tertiary">{tour[titleKey]}</h3>
                  <p className="txt_cont">{tour[descKey]}</p>
                </div>
              ) : null;
            })}
          </Pane>
        );

      case "gallery":
        return (
          <Pane>
            <h2 className="title-secondary">Gallery</h2>
            <div className="gallery">
              {Array.from({ length: 9 }, (_, i) => {
                const k = `image_gallery_${i + 1}`;
                return tour[k] ? (
                  <img
                    key={i}
                    src={`${process.env.REACT_APP_BASE_URL}/${tour[k]}`}
                    alt={`gallery-${i + 1}`}
                    className="gallery-image"
                  />
                ) : null;
              })}
            </div>
          </Pane>
        );

      default:
        return null;
    }
  };

  return (
    <Pane>
      <Tablist marginBottom={16}>
        {tabs.map((t, i) => (
          <Tab
            key={t.label}
            isSelected={i === selectedIndex}
            onSelect={() => setSelectedIndex(i)}
            style={{
              backgroundColor: i === selectedIndex ? "#f97316" : "#E6E8F0",
              color: i === selectedIndex ? "#fff" : "#000",
              padding: "25px 30px",
              borderRadius: "5px",
              margin: "0 5px",
              fontWeight: i === selectedIndex ? "bold" : "normal",
              fontSize: "18px",
            }}
          >
            {t.icon} {t.label}
          </Tab>
        ))}
      </Tablist>
      <Pane padding={16} background="tint1">
        {renderContent()}
      </Pane>
    </Pane>
  );
};

/* ------------------------------------------------------------------ */
/*  MESSAGE COMPONENT                                                 */
/* ------------------------------------------------------------------ */
const Message = ({ content, type = "info" }) => {
  if (!content) return null;
  const icons = { success: "Success", error: "Error", info: "Info" };
  return (
    <div className={`msg msg-${type}`}>
      <span className="msg-icon">{icons[type]}</span>
      <p>{content}</p>
    </div>
  );
};

/* --------------------- MAIN COMPONENT --------------------- */
export default function TourPackage() {
  const [searchParams] = useSearchParams();
  const tourId = searchParams.get("id");

  const [tour, setTour] = useState(null);
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    paymentType: "full",
    adult: 0,
    child: 0,
    date: "",
    amount: "0.00",
    remainingAmount: "0.00",
    message: "",
    destination_name: "",
    tourId: "",
  });

  const [loadingPesapal, setLoadingPesapal] = useState(false);
  const [statusMsg, setStatusMsg] = useState("");
  const [msgType, setMsgType] = useState("info");

  /* ------------------- FETCH TOUR ------------------- */
  useEffect(() => {
    if (!tourId) return;
    (async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/tours/${tourId}`);
        if (!res.ok) throw new Error("Network error");
        const data = await res.json();
        setTour(data);
        setFormData((p) => ({
          ...p,
          destination_name: data.destination_name,
          tourId,
        }));
      } catch (e) {
        console.error(e);
        toaster.danger("Failed to load tour details.");
      }
    })();
  }, [tourId]);

  /* ------------------- RECALC AMOUNT ------------------- */
  const recalc = useCallback(() => {
    if (!tour?.price) return;
    const adults = Number(formData.adult) || 0;
    const kids = Number(formData.child) || 0;
    const total = tour.price * (adults + kids);
    const deposit = formData.paymentType === "deposit";
    const paid = deposit ? total * 0.3 : total;
    const remain = deposit ? (total - paid).toFixed(2) : "0.00";

    setFormData((p) => ({
      ...p,
      amount: paid.toFixed(2),
      remainingAmount: remain,
    }));
  }, [tour?.price, formData.adult, formData.child, formData.paymentType]);

  useEffect(() => recalc(), [recalc]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    const intVal = ["adult", "child"].includes(name)
      ? Math.max(0, parseInt(value) || 0)
      : value;
    setFormData((p) => ({ ...p, [name]: intVal }));
  };

  /* ------------------- VALIDATION (stable) ------------------- */
  const validateForm = useCallback(() => {
    const required = {
      firstName: "First name is required",
      lastName: "Last name is required",
      email: "Email is required",
      phone: "Phone number is required",
      date: "Travel date is required",
    };

    for (const [field, message] of Object.entries(required)) {
      if (!formData[field] || formData[field].toString().trim() === "") {
        toaster.danger(message);
        return false;
      }
    }

    if (Number(formData.adult) + Number(formData.child) <= 0) {
      toaster.danger("Please add at least one traveler (adult or child)");
      return false;
    }

    if (Number(formData.amount) <= 0) {
      toaster.danger("Amount must be greater than zero");
      return false;
    }

    return true;
  }, [formData]);

  /* ------------------- PESAPAL ------------------- */
  const handlePesapal = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    setLoadingPesapal(true);
    try {
      const res = await fetch(`${API_BASE_URL}/pesapal/submit-order`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      });
      const { redirect_url } = await res.json();
      if (redirect_url) window.location.href = redirect_url;
    } catch (err) {
      console.error(err);
      toaster.danger("Pesapal payment failed. Please try again.");
    } finally {
      setLoadingPesapal(false);
    }
  };

  /* ------------------- PAYPAL (refactored) ------------------- */
  const paypalOptions = useMemo(
    () => ({
      "client-id": PAYPAL_CLIENT_ID,
      currency: "USD",
      intent: "capture",
      components: "buttons,messages",
      "enable-funding": "paypal,card",
      // "buyer-country": "KE",
      "data-page-type": "checkout",
      "data-sdk-integration-source": "button-factory",
    }),
    []
  );

  const createOrder = useCallback(async () => {
    if (!validateForm()) {
      throw new Error("Please fill in all required fields.");
    }

    try {
      setStatusMsg("");
      const amount = parseFloat(formData.amount);
      if (isNaN(amount) || amount <= 0)
        throw new Error("Please enter a valid amount.");

      const description = `${formData.destination_name || "Tour"} – ${
        formData.adult
      } adult(s), ${formData.child} child(ren)`;

      const payload = {
        intent: "CAPTURE",
        purchase_units: [
          {
            amount: { currency_code: "USD", value: amount.toFixed(2) },
            description,
          },
        ],
      };

      const bookingPayload = {
        ...formData,
        description,
        currency: "USD",
        payment_method: "PayPal",
        paypalPayload: payload,
      };

      const res = await fetch(`${API_BASE_URL}/paypal/create-order`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(bookingPayload),
      });

      const data = await res.json();
      if (!res.ok || !data?.id)
        throw new Error(data?.error || "Failed to create PayPal order.");

      console.log("PayPal order created:", data.id);
      return data.id;
    } catch (err) {
      console.error("createOrder error:", err);
      setStatusMsg(`Checkout error: ${err.message}`);
      setMsgType("error");
      toaster.danger(err.message || "PayPal checkout failed");
      throw err;
    }
  }, [formData, validateForm]);

  const onApprove = async (data) => {
    try {
      console.log("Payment approved:", data.orderID);
      const res = await fetch(
        `${API_BASE_URL}/paypal/capture-order/${data.orderID}`,
        { method: "POST", headers: { "Content-Type": "application/json" } }
      );

      const capture = await res.json();
      if (!res.ok || !capture?.captureId) {
        throw new Error(capture?.error || "Capture failed");
      }

      sessionStorage.setItem(
  "paypal_receipt",
  JSON.stringify({
    ...capture,
    orderID: data.orderID   // Correctly added to the object
  })
);

window.location.href = "/paypal/success";
    } catch (err) {
      console.error("Capture error:", err);
      setStatusMsg("Payment failed: " + err.message);
      setMsgType("error");
      toaster.danger("Payment capture failed");
    }
  };

  const onError = useCallback((err) => {
    console.error("PayPal error:", err);
    setStatusMsg("PayPal error — please refresh and try again.");
    setMsgType("error");
    toaster.danger("PayPal error occurred. Please try again.");
  }, []);

  /* ------------------- INIT ------------------- */
  useEffect(() => {
    window.scrollTo(0, 0);
    AOS.init();
    AOS.refresh();
  }, []);

  if (!tour) return <div className="loading-tour">Loading tour…</div>;

  return (
    <div className="package_container">
      {/* No <Toaster /> component – toasts are shown imperatively */}

      <div className="package-sect">
        <p className="banner-title">Tour Packages</p>
      </div>

      <div data-aos="zoom-in" className="package_sides">
        {/* LEFT SECTION */}
        <div className="rt_package">
          <div className="image-holder">
            <img
              src={`${API_BASE_URL}/${tour.image_preview}`}
              alt={tour.destination_name}
              className="img-package"
            />
          </div>

          <div className="package-titles">
            <p
              className="card__title"
              style={{ color: "black", fontWeight: 700 }}
            >
              {tour.destination_name}, {tour.destination_title}
            </p>
          </div>

          <hr
            style={{
              margin: "20px 0",
              backgroundColor: "#ccc",
              border: "none",
              height: "1px",
            }}
          />

          <div className="info-breaker">
            <div className="package-icons">
              <CiClock2 className="icon-package" />
              <div>
                <p className="title-primary" style={{ fontSize: 17, fontWeight: 700 }}>
                  Duration
                </p>
                <p className="txt_cont" style={{ fontSize: 15, fontWeight: 800, padding: 3 }}>
                  {tour.duration} days
                </p>
              </div>
            </div>

            <div className="package-icons">
              <IoPricetagOutline className="icon-package" />
              <div>
                <p className="title-primary" style={{ fontSize: 17, fontWeight: 700 }}>
                  Price
                </p>
                <p className="txt_cont" style={{ fontSize: 15, fontWeight: 800, padding: 3 }}>
                  ${tour.price}
                </p>
              </div>
            </div>

            <div className="package-icons">
              <CiStar className="icon-package" />
              <div>
                <p className="title-primary" style={{ fontSize: 17, fontWeight: 700 }}>
                  Rating
                </p>
                <p className="txt_cont" style={{ fontSize: 15, fontWeight: 800, padding: 3 }}>
                  8K+
                </p>
              </div>
            </div>

            <div className="package-icons">
              <IoPersonOutline className="icon-package" />
              <div>
                <p className="title-primary" style={{ fontSize: 17, fontWeight: 700 }}>
                  Age
                </p>
                <p className="txt_cont" style={{ fontSize: 15, fontWeight: 800, padding: 3 }}>
                  2+
                </p>
              </div>
            </div>
          </div>

          <hr
            style={{
              margin: "20px 0",
              backgroundColor: "#ccc",
              border: "none",
              height: "1px",
            }}
          />

          <Tabs tour={tour} />
        </div>

        {/* RIGHT SECTION - BOOKING FORM */}
     
<div className="lt_package">
  <Text fontSize={18} fontWeight={700} textAlign="center" marginBottom={majorScale(3)}>
    Book this Package
  </Text>

  <form onSubmit={handlePesapal} className="booking-inputs">
    <Pane marginBottom={majorScale(2)}>
      <Text display="block" marginBottom={majorScale(1)}>
        First Name <Text color="red">*</Text>
      </Text>
      <TextInput
        name="firstName"
        value={formData.firstName}
        onChange={handleChange}
        placeholder="Enter first name"
        width="100%"
        required
      />
    </Pane>

    <Pane marginBottom={majorScale(2)}>
      <Text display="block" marginBottom={majorScale(1)}>
        Last Name <Text color="red">*</Text>
      </Text>
      <TextInput
        name="lastName"
        value={formData.lastName}
        onChange={handleChange}
        placeholder="Enter last name"
        width="100%"
        required
      />
    </Pane>

    <Pane marginBottom={majorScale(2)}>
      <Text display="block" marginBottom={majorScale(1)}>
        Email <Text color="red">*</Text>
      </Text>
      <TextInput
        type="email"
        name="email"
        value={formData.email}
        onChange={handleChange}
        placeholder="Enter email address"
        width="100%"
        required
      />
    </Pane>

    <Pane marginBottom={majorScale(2)}>
      <Text display="block" marginBottom={majorScale(1)}>
        Phone <Text color="red">*</Text>
      </Text>
      <TextInput
        type="tel"
        name="phone"
        value={formData.phone}
        onChange={handleChange}
        placeholder="Enter phone number"
        width="100%"
        required
      />
    </Pane>

    <Pane marginBottom={majorScale(2)}>
      <Text display="block" marginBottom={majorScale(1)}>
        Number of Adults <Text color="red">*</Text>
      </Text>
      <Select name="adult" value={formData.adult} onChange={handleChange} width="100%">
        {[...Array(11).keys()].map((n) => (
          <option key={n} value={n}>
            {n}
          </option>
        ))}
      </Select>
    </Pane>

    <Pane marginBottom={majorScale(2)}>
      <Text display="block" marginBottom={majorScale(1)}>
        Number of Children <Text color="red">*</Text>
      </Text>
      <Select name="child" value={formData.child} onChange={handleChange} width="100%">
        {[...Array(11).keys()].map((n) => (
          <option key={n} value={n}>
            {n}
          </option>
        ))}
      </Select>
    </Pane>

    <Pane marginBottom={majorScale(2)}>
      <Text display="block" marginBottom={majorScale(1)}>
        Date of Travel <Text color="red">*</Text>
      </Text>
      <DatePicker
        selected={formData.date ? new Date(formData.date) : null}
        onChange={(date) =>
          handleChange({
            target: { name: "date", value: date.toISOString().split("T")[0] },
          })
        }
        minDate={new Date()}
        placeholderText="Select a travel date"
        dateFormat="yyyy-MM-dd"
        className="datepicker-input"
      />
    </Pane>

    <Pane marginBottom={majorScale(2)}>
      <Text display="block" marginBottom={majorScale(1)}>
        Payment Type
      </Text>
      <Select name="paymentType" value={formData.paymentType} onChange={handleChange} width="100%">
        <option value="full">Full</option>
        <option value="deposit">Deposit (30%)</option>
      </Select>
    </Pane>

    <Pane marginBottom={majorScale(2)} className="amount-display">
      <Text fontWeight={600}>Amount: ${formData.amount}</Text>
      {formData.paymentType === "deposit" && (
        <Text color="gray700">Remaining: ${formData.remainingAmount}</Text>
      )}
    </Pane>

    <Pane marginBottom={majorScale(2)}>
      <Text display="block" marginBottom={majorScale(1)}>
        Message
      </Text>
      <Textarea
        name="message"
        value={formData.message}
        onChange={handleChange}
        placeholder="Additional message..."
        height={120}
        width="100%"
      />
    </Pane>

    <Button
      appearance="primary"
      intent="success"
      type="submit"
      disabled={loadingPesapal}
      width="100%"
      marginTop={majorScale(1)}
    >
      {loadingPesapal ? <Spinner size={16} /> : "Pay Now (Pesapal - Mpesa, Airtel & Bank)"}
    </Button>
  </form>

  <hr className="divider" />

  <div className="paypal-container">
  <PayPalScriptProvider
    options={{
      ...paypalOptions,
      dataShippingPreference: "NO_SHIPPING",
    }}
  >
    <PayPalMessages />
    <Message content={statusMsg} type={msgType} />

    <PayPalButtons
      fundingSource="paypal"
      style={{
        layout: "vertical",
        color: "gold",
        shape: "rect",
        label: "paypal",
        height: 45,
      }}
      createOrder={createOrder}
      onApprove={onApprove}
      onError={onError}
      dataShippingPreference="NO_SHIPPING"
    />

    <div style={{ marginTop: "15px" }}>
      <PayPalButtons
        fundingSource="card"
        style={{
          layout: "vertical",
          color: "black",
          shape: "rect",
          label: "pay",
          height: 45,
        }}
        createOrder={createOrder}
        onApprove={onApprove}
        onError={onError}
        dataShippingPreference="NO_SHIPPING"
      />
    </div>
  </PayPalScriptProvider>
</div>
</div>

      </div>

      <Newsletter />
    </div>
  );
}